/**
 * VM que existe no cluster com a criação pela metade (issue #803).
 *
 * O backend mantém a instância em `creating` quando a criação é interrompida
 * depois de a VM existir no Proxmox, e a reconciliação a promove a `running` ao
 * conciliar com o cluster. A partir daí, `status` não distingue mais uma VM
 * provisionada por inteiro de uma resgatada pela metade — mas a cauda da criação
 * não rodou: credencial de cloud-init, IP e `nameserver` são gravados depois do
 * clone, e o resize para o disco contratado é posterior ainda.
 *
 * Por isso a régua é o campo próprio, nunca o `status`.
 */

export type VMComProvisionamento = {
  /** Ausente em backend anterior à #803 — ver `provisionamentoIncompleto`. */
  provisioning_incomplete?: boolean
  /** Quando a criação foi interrompida. Só o detalhe traz. */
  provisioning_incomplete_at?: string | null
}

/**
 * O aviso a mostrar na VM. Fica aqui, e não espalhado nas telas, porque a
 * listagem e o detalhe têm de dizer a MESMA coisa: é a única orientação que o
 * cliente recebe, e o motivo técnico gravado no banco (mensagem do PVE, etapa da
 * criação) não sai do backend de propósito — ele serve ao suporte.
 */
export const AVISO_PROVISIONAMENTO_INCOMPLETO = {
  titulo: 'Provisionamento não concluído',
  mensagem:
    'A criação desta VM foi interrompida depois que ela já existia no servidor. ' +
    'As credenciais de acesso, a rede ou o disco contratado podem não ter sido ' +
    'aplicados — acione o suporte antes de usá-la.',
} as const

/**
 * `=== true`, e não `!== false`: o campo é NOVO, e o front e o backend deployam
 * separados (Cloudflare Pages e Fly, sem ordenação). Durante a janela em que o
 * portal novo fala com a API antiga, `provisioning_incomplete` chega `undefined`
 * — e `!== false` transformaria TODA VM da frota num alerta de "acione o
 * suporte". Falhar fechado aqui repõe o comportamento de hoje (nenhum aviso),
 * que é o pior caso conhecido, em vez de inventar um pânico novo.
 */
export function provisionamentoIncompleto(vm: VMComProvisionamento | null | undefined): boolean {
  return vm?.provisioning_incomplete === true
}

/**
 * A data da interrupção, formatada para o aviso — ou `null` quando não há.
 *
 * Responde "desde quando", que é o que o cliente pergunta ao ver o aviso e a única
 * razão de o backend expor o campo. Data inválida (ou ausente) devolve `null` e o
 * aviso fica sem a frase: melhor sem a informação do que com um "Invalid Date".
 */
export function desdeQuandoIncompleto(vm: VMComProvisionamento | null | undefined): string | null {
  const bruta = vm?.provisioning_incomplete_at
  if (!bruta) return null
  const data = new Date(bruta)
  if (Number.isNaN(data.getTime())) return null
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
